import { authClient } from '@yoreflix/core/api/authClient'
import { apiUrl } from '@yoreflix/core/api/config'

/**
 * The upload portal's API client — `yoreflix/v1/upload/*`.
 *
 * ★★ NO CALL NAMES AN ORGANIZATION, and there is no parameter to pass one. The server resolves
 * whose data this is from the authenticated session (`OrgScope`) and re-checks ownership on every
 * mutation. v1 trusted a client-supplied key here and let any authenticated uploader edit another
 * supplier's project; the absence of an org argument is what makes that impossible to reintroduce
 * by accident.
 */

export interface UploadFileRow {
  PrimaryKey: string
  Name: string
  SizeBytes: number
  Status: 'uploading' | 'complete' | string
  CreatedDtInt: number
  CompletedDtInt: number
}

export interface UploadProject {
  PrimaryKey: string
  Name: string
  Description: string
  Notes: string
  CreatedDtInt: number
  Files: UploadFileRow[]
}

export interface ProjectsResponse {
  Linked: boolean
  Reason?: string
  Projects: UploadProject[]
}

/**
 * The tus creation endpoint, absolute — tus-js-client builds its own requests and cannot use
 * authClient's relative paths. Derived from the same host-based resolver as everything else, so
 * `.uk` and `.com` need no build-time switch.
 */
export function tusEndpoint(): string {
  return apiUrl('upload/files')
}

export const uploadClient = {
  projects(): Promise<ProjectsResponse> {
    return authClient.request<ProjectsResponse>('GET', 'upload/projects')
  },

  createProject(name: string, description = '', notes = ''): Promise<{ Linked: boolean; Project: UploadProject | null }> {
    return authClient.request('POST', 'upload/projects', { Name: name, Description: description, Notes: notes })
  },

  updateProject(key: string, patch: Partial<Pick<UploadProject, 'Name' | 'Description' | 'Notes'>>): Promise<{ Project: UploadProject | null }> {
    return authClient.request('POST', `upload/projects/${encodeURIComponent(key)}`, patch)
  },

  /**
   * ⚠ Refusal rides the SUCCESS channel: a project holding completed files answers
   * `{Deleted:false, Reason:'has_completed_files'}` with HTTP 200, because 409 belongs to the auth
   * transport and the client would refresh-and-retry it forever.
   */
  deleteProject(key: string): Promise<{ Deleted: boolean; Reason?: string; CompletedFiles?: number }> {
    return authClient.request('POST', `upload/projects/${encodeURIComponent(key)}/delete`)
  },
}

/**
 * Bytes for humans — v1's exact formatting, shared with the admin portal.
 *
 * ⚠ Re-exported rather than reimplemented: three formatters would drift and the same delivery
 * would end up with two different sizes on two screens. See @yoreflix/core/format/fileSize for why
 * it copies v1 down to the rounding.
 */
export { formatFileSize as fileSize } from '@yoreflix/core/format/fileSize'
